class CartCountdownTimer {
  constructor() {
    this.storageKey = 'cartTimerStart';
    this.isExpired = false;
    this.intervalId = null;
    this.observer = null;
    
    // Start monitoring immediately
    this.startMonitoring();
    
    // Set up observer to watch for cart drawer changes
    this.setupCartObserver();
  }
  
  setupCartObserver() {
    // Watch for changes to the cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if timer elements were added
            const hasTimerElements = Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.querySelector('#cart-timer-countdown') || 
                       node.querySelector('#cart-timer-text') ||
                       node.id === 'cart-timer-countdown' ||
                       node.id === 'cart-timer-text';
              }
              return false;
            });
            
            if (hasTimerElements) {
              setTimeout(() => this.startMonitoring(), 100);
            }
          }
        });
      });
      
      this.observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  }
  
  startMonitoring() {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    // Check if timer elements exist
    const timerElement = document.getElementById('cart-timer-countdown');
    const timerTextElement = document.getElementById('cart-timer-text');
    
    if (!timerElement || !timerTextElement) {
      // Retry in 500ms
      setTimeout(() => this.startMonitoring(), 500);
      return;
    }
    
    // Start the countdown
    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }
  
  tick() {
    // Get elements fresh each time
    const timerElement = document.getElementById('cart-timer-countdown');
    const timerTextElement = document.getElementById('cart-timer-text');
    const timerBarElement = document.getElementById('cart-timer-bar');
    
    // If elements don't exist, skip this tick
    if (!timerElement || !timerTextElement) {
      return;
    }
    
    // Store original text if not already stored
    if (!timerTextElement.getAttribute('data-original-text')) {
      timerTextElement.setAttribute('data-original-text', timerTextElement.innerHTML);
    }
    
    // Get configuration from data attributes
    const durationMinutes = parseInt(timerTextElement.dataset.minutes) || 1;
    const expiredMessage = timerTextElement.dataset.expired || 'Time expired!';
    const durationMs = durationMinutes * 60 * 1000;
    
    // Check if timer already exists in sessionStorage
    let startTime = sessionStorage.getItem(this.storageKey);
    
    if (!startTime && !this.isExpired) {
      // First time - set start time
      startTime = Date.now();
      sessionStorage.setItem(this.storageKey, startTime);
    }
    
    if (!startTime) {
      return;
    }
    
    startTime = parseInt(startTime);
    
    const now = Date.now();
    const elapsed = now - startTime;
    const remaining = durationMs - elapsed;
    
    if (remaining <= 0 && !this.isExpired) {
      // Timer expired - keep it in sessionStorage, it will clear on window close
      timerTextElement.innerHTML = expiredMessage;
      
      if (timerBarElement) {
        timerBarElement.classList.add('timer-expired');
      }
      
      this.isExpired = true;
      
      // Clear interval when expired
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }
    
    if (remaining > 0) {
      // Calculate minutes and seconds
      const totalSeconds = Math.floor(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      // Format time as M:SS or MM:SS
      const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      // Update the display
      timerElement.textContent = formattedTime;
    }
  }
  
  // Method to reset timer (can be called externally if needed)
  reset() {
    this.isExpired = false;
    sessionStorage.removeItem(this.storageKey);
    
    // Clear existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    // Restart monitoring
    this.startMonitoring();
  }
  
  // Cleanup method
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize timer when DOM is ready
function initializeTimer() {
  // Clear any existing timer
  if (window.cartTimer) {
    window.cartTimer.destroy();
  }
  
  window.cartTimer = new CartCountdownTimer();
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTimer);
} else {
  initializeTimer();
}

// Also reinitialize on cart events
document.addEventListener('cart:refresh', () => {
  setTimeout(initializeTimer, 200);
});

document.addEventListener('cart:change', () => {
  setTimeout(initializeTimer, 200);
});

// For debugging
window.getTimerInfo = function() {
  const startTime = sessionStorage.getItem('cartTimerStart');
  if (startTime) {
    const timerTextElement = document.getElementById('cart-timer-text');
    const durationMinutes = timerTextElement ? (parseInt(timerTextElement.dataset.minutes) || 1) : 1;
    const durationMs = durationMinutes * 60 * 1000;
    const elapsed = Date.now() - parseInt(startTime);
    const remaining = durationMs - elapsed;
    return {
      start: new Date(parseInt(startTime)),
      elapsed: Math.floor(elapsed / 1000) + ' seconds',
      remaining: Math.floor(remaining / 1000) + ' seconds'
    };
  } else {
    return 'Timer not started yet';
  }
};